import { parseApiError } from '../utils/apiErrorParser';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper function to get API role from UI role
const getApiRole = (uiRole) => {
  const roleMapping = {
    'SuperAdmin': 'superadmin',
    'Finance': 'finance',
    'Ops': 'ops',
    'Support': 'support'
  };
  return roleMapping[uiRole] || 'ops';
};

// Helper function to make API requests
const makeRequest = async (endpoint, options = {}) => {
  try {
    const token = localStorage.getItem('authToken');
    
    // Check if token exists
    if (!token) {
      throw new Error('No authentication token found. Please login first.');
    }
    
    const fullUrl = `${BASE_URL}${endpoint}`;
    
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    const requestOptions = {
      method: options.method || 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      signal: controller.signal
    };
    
    // Include body for POST, PUT, PATCH, DELETE requests
    if (options.body && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(requestOptions.method)) {
      requestOptions.body = options.body;
    }
    
    const response = await fetch(fullUrl, requestOptions);
    
    clearTimeout(timeoutId);

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authRole');
        localStorage.removeItem('anchorId');
        window.location.href = '/login';
        return;
      }

      const errorText = await response.text();

      let message = '';

      try {
        message = parseApiError(JSON.parse(errorText));
      } catch {
        message = errorText || `Request failed (${response.status})`;
      }
      
      const httpError = new Error(message);
      httpError.status = response.status;
      throw httpError;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - backend may not be running');
    }
    
    throw error;
  }
};

// Get subscriber profile
export const getSubscriberProfile = async (applicationId, role) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/applications/${applicationId}/profile/`);
};

// Get wallet snapshot
export const getWalletSnapshot = async (applicationId, role) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/applications/${applicationId}/snapshot/`;
  
  // Wallet snapshot API call
  
  return makeRequest(endpoint);
};


// Get Ayushpay Credit summary — activation status + the 5-tile credit snapshot
export const getSubscriberCreditSummary = async (applicationId, role) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/applications/${applicationId}/credit-summary/`;
  return makeRequest(endpoint);
};

// Get transaction history
export const getTransactionHistory = async (applicationId, role, params = {}) => {
  const apiRole = getApiRole(role);
  
  // Build query string only if params exist
  let endpoint = `/internal/apis/v4/${apiRole}/applications/${applicationId}/transactions/`;
  
  if (params && Object.keys(params).length > 0) {
    const queryString = new URLSearchParams(params).toString();
    endpoint = `/internal/apis/v4/${apiRole}/applications/${applicationId}/transactions/?${queryString}`;
  }
  
  return makeRequest(endpoint);
};

// Get transaction detail
export const getTransactionDetail = async (applicationId, transactionId, role) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/applications/${applicationId}/transactions/${transactionId}/detail/`;
  return makeRequest(endpoint);
};

// Get redemption history
export const getRedemptionHistory = async (applicationId, role, params = {}) => {
  const apiRole = getApiRole(role);
  
  // Build query string only if params exist
  let endpoint = `/internal/apis/v4/${apiRole}/applications/${applicationId}/redemptions/`;
  
  if (params && Object.keys(params).length > 0) {
    const queryString = new URLSearchParams(params).toString();
    endpoint = `/internal/apis/v4/${apiRole}/applications/${applicationId}/redemptions/?${queryString}`;
  }
  
  return makeRequest(endpoint);
};

// Get bank details
export const getBankDetails = async (applicationId, role) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/applications/${applicationId}/bank-details/`);
};

// Get audit logs
export const getAuditLogs = async (applicationId, role) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/applications/${applicationId}/audit-logs/`);
};

// Get family members.
// Pass { activeOnly: true } for booking flows where deactivated members must
// not appear. Default (no options) returns all members — the member-management
// page relies on this so ops can see and reactivate inactive members. Fixes
// AP-823: the frontend used to filter on a non-existent `active` boolean; the
// serializer actually emits `status: 'Active' | 'Inactive'` as a string, so
// the filter was a no-op. Pushing the filter server-side is the durable fix.
export const getFamilyMembers = async (applicationId, role, { activeOnly = false } = {}) => {
  const apiRole = getApiRole(role);
  const statusSuffix = activeOnly ? '&status=active' : '';
  return makeRequest(`/internal/apis/v4/${apiRole}/applications/${applicationId}/family-members/?items=25${statusSuffix}`);
};

// Add family member (Ops and SuperAdmin only)
export const addFamilyMember = async (applicationId, role, memberData) => {
  const apiRole = getApiRole(role);
  
  const response = await makeRequest(`/internal/apis/v4/${apiRole}/applications/${applicationId}/family-members/add/`, {
    method: 'POST',
    body: JSON.stringify(memberData)
  });
  return response;
};

// Update redemption status (Finance and SuperAdmin only)
export const updateRedemptionStatus = async (applicationId, role, redemptionId, action) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/applications/${applicationId}/redemptions/${redemptionId}/`, {
    method: 'PATCH',
    body: JSON.stringify({ action })
  });
};

// Update family member (Ops and SuperAdmin)
export const updateFamilyMember = async (applicationId, memberId, role, memberData) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/applications/${applicationId}/family-members/${memberId}/edit/`, {
    method: 'PUT',
    body: JSON.stringify(memberData)
  });
};

// Deactivate family member (Ops and SuperAdmin)
export const deactivateFamilyMember = async (applicationId, memberId, role, reasonCode) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/applications/${applicationId}/family-members/${memberId}/deactivate/`, {
    method: 'POST',
    body: JSON.stringify({
      reason_code: reasonCode,
      type: 'DEACTIVATE'
    })
  });
};

// Get applications list
export const getApplicationsList = async (role, params = {}) => {
  const apiRole = getApiRole(role);
  const queryString = new URLSearchParams(params).toString();
  
  const endpoint = queryString 
    ? `/internal/apis/v4/${apiRole}/applications/list/?${queryString}`
    : `/internal/apis/v4/${apiRole}/applications/list/`;
  const response = await makeRequest(endpoint);
  return response;
};

// Download Excel for applications - SuperAdmin and Finance only
export const downloadApplicationsExcel = async (role, filters = {}) => {
  if (role !== 'SuperAdmin' && role !== 'Finance') {
    throw new Error('Access denied. Only SuperAdmin and Finance can download Excel.');
  }
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/applications/download-excel/`;

  try {
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      throw new Error('No authentication token found. Please login first.');
    }
    
    const fullUrl = `${BASE_URL}${endpoint}`;
    
    // Build request body - only custom date range is allowed
  const requestBody = {
      date_filter: 'custom',
    start_date: filters.start_date || '',
      end_date: filters.end_date || ''
  };
  
    const response = await fetch(fullUrl, {
    method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    body: JSON.stringify(requestBody)
  });
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails = '';
      
      try {
        const errorJson = JSON.parse(errorText);
        errorDetails = JSON.stringify(errorJson, null, 2);
      } catch {
        errorDetails = errorText;
      }
      
      throw new Error(`API Error ${response.status} (${response.statusText}): ${errorDetails}`);
    }
    
    // Always expect JSON response (WhatsApp notification)
    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - backend may not be running');
    }
    throw error;
  }
};

// Create manual transaction
export const createManualTransaction = async (applicationId, role, transactionData) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/applications/${applicationId}/transactions/create/`;
  
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(transactionData)
  });
};

// Get all hospital payments list (Ops and SuperAdmin only)
export const getHospitalPayments = async (role, params = {}) => {
  const apiRole = getApiRole(role);
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.items) queryParams.append('items', params.items.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.date_filter) {
    queryParams.append('date_filter', params.date_filter);
    if (params.date_filter === 'custom' && params.start_date && params.end_date) {
      queryParams.append('start_date', params.start_date);
      queryParams.append('end_date', params.end_date);
    }
  }
  if (params.status && Array.isArray(params.status)) {
    params.status.forEach(s => queryParams.append('status', s));
  } else if (params.status) {
    queryParams.append('status', params.status);
  }
  const queryString = queryParams.toString();
  const endpoint = queryString
    ? `/internal/apis/v4/${apiRole}/hospital-payment/all/?${queryString}`
    : `/internal/apis/v4/${apiRole}/hospital-payment/all/`;
  return makeRequest(endpoint);
};

// Get single hospital booking detail (Ops and SuperAdmin only)
export const getHospitalBookingDetail = async (role, bookingId) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/hospital-payment/${bookingId}/detail/`);
};

// Get all hospital bookings for a specific application
export const getApplicationHospitalBookings = async (role, applicationId, params = {}) => {
  const apiRole = getApiRole(role);
  const queryParams = new URLSearchParams();
  queryParams.append('application_id', applicationId);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.items) queryParams.append('items', params.items.toString());
  const endpoint = `/internal/apis/v4/${apiRole}/hospital-payment/list/?${queryParams.toString()}`;
  return makeRequest(endpoint);
};

// Create hospital booking (Ops and SuperAdmin only)
export const createHospitalBooking = async (role, bookingData) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/hospital-payment/create/`;
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(bookingData)
  });
};

// Move cashback for a hospital booking (Ops and SuperAdmin only)
export const moveCashback = async (role, bookingId) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/hospital-payment/move-cashback/`;
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify({ booking_id: bookingId })
  });
};

// Get redemption requests list (Finance and SuperAdmin only)
export const getRedemptionRequestsList = async (role, params = {}) => {
  const apiRole = getApiRole(role);
  const queryString = new URLSearchParams(params).toString();
  
  const endpoint = queryString 
    ? `/internal/apis/v4/${apiRole}/redemption-requests/list/?${queryString}`
    : `/internal/apis/v4/${apiRole}/redemption-requests/list/`;
  const response = await makeRequest(endpoint);
  return response;
};

// Approve redemption request (Finance and SuperAdmin only)
export const approveRedemptionRequest = async (role, requestId) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/redemption-requests/${requestId}/approve/`;
  
  return makeRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify({ actioned_at: 'Approve' })
  });
};

// Get service providers by service type
export const getServiceProviders = async (serviceType) => {
  // Service types match backend choices exactly
  const serviceTypeMap = {
    'medicine': 'medicine',
    'diagnostics': 'diagnostics',
    'hospitalization': 'hospitalization',
    'loan': 'loan',
    'hospital_cash_allowance': 'hospital_cash_allowance'
  };
  
  // Get mapped value or use the serviceType as-is
  const apiServiceType = serviceTypeMap[serviceType] || serviceType.toLowerCase();
  
  // Validate that we have a valid service type
  const validServiceTypes = ['medicine', 'diagnostics', 'hospitalization', 'loan', 'hospital_cash_allowance'];
  if (!validServiceTypes.includes(apiServiceType)) {
    throw new Error(`Invalid service_type: ${apiServiceType}. Valid options: ${validServiceTypes.join(', ')}`);
  }
  
  const endpoint = `/internal/apis/v4/vendors/service-providers/?service_type=${apiServiceType}`;
  
  return makeRequest(endpoint);
};

// Get cities list (for hospital/hospital_cash_allowance service types)
// @param {string} searchText - Optional search term for city or state
// @param {string} role - User role (Finance, Ops, SuperAdmin)
export const getCities = async (searchText = '', role = null) => {
  // Get user role from localStorage if not provided
  if (!role) {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = {
      'wallet_superadmin': 'superadmin',
      'wallet_finance': 'finance',
      'wallet_ops': 'ops',
      'wallet_support': 'support'
    };
    role = roleMapping[authRole] || 'ops';
  }
  
  const apiRole = getApiRole(role);
  let endpoint = `/internal/apis/v4/${apiRole}/hospitals/cities/?items=1000`;
  
  // Add filters if search text is provided
  if (searchText && searchText.trim()) {
    const filters = JSON.stringify([{ key: 'city', value: searchText.trim() }]);
    endpoint += `&filters=${encodeURIComponent(filters)}`;
  }
  
  return makeRequest(endpoint);
};

// Get hospitals list (for hospital/hospital_cash_allowance service types)
// @param {string} city - City name (required)
// @param {string} state - State name (recommended for accuracy)
// @param {string} nameFilter - Optional search term for hospital name
// @param {string} role - User role (Finance, Ops, SuperAdmin)
export const getHospitalsByCity = async (city, state = '', nameFilter = '', role = null) => {
  if (!city) {
    throw new Error('City is required to fetch hospitals');
  }
  
  // Get user role from localStorage if not provided
  if (!role) {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = {
      'wallet_superadmin': 'superadmin',
      'wallet_finance': 'finance',
      'wallet_ops': 'ops',
      'wallet_support': 'support'
    };
    role = roleMapping[authRole] || 'ops';
  }
  
  const apiRole = getApiRole(role);
  
  // Build filters array
  const filters = [{ key: 'city', value: city }];
  
  // Add state filter if provided (recommended for accuracy)
  if (state && state.trim()) {
    filters.push({ key: 'state', value: state.trim() });
  }
  
  // Add name filter if provided (for search functionality)
  if (nameFilter && nameFilter.trim()) {
    filters.push({ key: 'name', value: nameFilter.trim() });
  }
  
  const filtersParam = encodeURIComponent(JSON.stringify(filters));
  const endpoint = `/internal/apis/v4/${apiRole}/hospitals/list/?filters=${filtersParam}&items=1000`;
  
  return makeRequest(endpoint);
};

// Get diagnostic subscribers list
export const getDiagnosticSubscribers = async (role, params = {}) => {
  const apiRole = getApiRole(role);
  
  // Build query parameters
  const queryParams = new URLSearchParams();
  
  // Add pagination
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.items) queryParams.append('items', params.items.toString());
  
  // Add search
  if (params.search) queryParams.append('search', params.search);
  
  // Add date filter
  if (params.date_filter) {
    queryParams.append('date_filter', params.date_filter);
    if (params.date_filter === 'custom' && params.start_date && params.end_date) {
      queryParams.append('start_date', params.start_date);
      queryParams.append('end_date', params.end_date);
    }
  }
  
  // Add status filters (can be multiple)
  if (params.status && Array.isArray(params.status)) {
    params.status.forEach(status => queryParams.append('status', status));
  } else if (params.status) {
    queryParams.append('status', params.status);
  }
  
  // Add service provider filters (can be multiple)
  if (params.service_provider && Array.isArray(params.service_provider)) {
    params.service_provider.forEach(sp => queryParams.append('service_provider', sp));
  } else if (params.service_provider) {
    queryParams.append('service_provider', params.service_provider);
  }
  
  const queryString = queryParams.toString();
  const endpoint = queryString 
    ? `/internal/apis/v4/${apiRole}/diagnostic-subscribers/list/?${queryString}`
    : `/internal/apis/v4/${apiRole}/diagnostic-subscribers/list/`;
  
  const response = await makeRequest(endpoint);
  return response;
};

// Get diagnostic booking snapshot
export const getDiagnosticBookingSnapshot = async (role, bookingId, params = {}) => {
  const apiRole = getApiRole(role);
  
  // Build query parameters
  const queryParams = new URLSearchParams();
  
  // Add date range filters
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  
  // Add status filters (can be multiple)
  if (params.status && Array.isArray(params.status)) {
    params.status.forEach(status => queryParams.append('status', status));
  } else if (params.status) {
    queryParams.append('status', params.status);
  }
  
  // Add pagination
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.items) queryParams.append('items', params.items.toString());
  
  const queryString = queryParams.toString();
  const endpoint = queryString 
    ? `/internal/apis/v4/${apiRole}/diagnostic-subscribers/${bookingId}/snapshot/?${queryString}`
    : `/internal/apis/v4/${apiRole}/diagnostic-subscribers/${bookingId}/snapshot/`;
  
  const response = await makeRequest(endpoint);
  return response;
};

// Get diagnostic booking detail
export const getDiagnosticBookingDetail = async (role, bookingId) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/diagnostic-subscribers/${bookingId}/detail/`;
  
  const response = await makeRequest(endpoint);
  return response;
};

// -----------------------------
// Medicine Subscribers APIs
// -----------------------------

// Get medicine subscribers list
export const getMedicineSubscribers = async (role, params = {}) => {
  const apiRole = getApiRole(role);

  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', params.page.toString());
  if (params.items) queryParams.append('items', params.items.toString());
  if (params.search) queryParams.append('search', params.search);

  if (params.date_filter) {
    queryParams.append('date_filter', params.date_filter);
    if (params.date_filter === 'custom' && params.start_date && params.end_date) {
      queryParams.append('start_date', params.start_date);
      queryParams.append('end_date', params.end_date);
    }
  }

  if (params.status && Array.isArray(params.status)) {
    params.status.forEach(status => queryParams.append('status', status));
  } else if (params.status) {
    queryParams.append('status', params.status);
  }

  if (params.service_provider && Array.isArray(params.service_provider)) {
    params.service_provider.forEach(sp => queryParams.append('service_provider', sp));
  } else if (params.service_provider) {
    queryParams.append('service_provider', params.service_provider);
  }

  if (params.updated_by && Array.isArray(params.updated_by)) {
    params.updated_by.forEach(u => queryParams.append('updated_by', u));
  } else if (params.updated_by) {
    queryParams.append('updated_by', params.updated_by);
  }

  const queryString = queryParams.toString();
  const endpoint = queryString
    ? `/internal/apis/v4/${apiRole}/medicine-subscribers/list/?${queryString}`
    : `/internal/apis/v4/${apiRole}/medicine-subscribers/list/`;

  return makeRequest(endpoint);
};

// Get medicine booking snapshot
export const getMedicineBookingSnapshot = async (role, bookingId, params = {}) => {
  const apiRole = getApiRole(role);

  const queryParams = new URLSearchParams();

  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);

  if (params.status && Array.isArray(params.status)) {
    params.status.forEach(status => queryParams.append('status', status));
  } else if (params.status) {
    queryParams.append('status', params.status);
  }

  if (params.page) queryParams.append('page', params.page.toString());
  if (params.items) queryParams.append('items', params.items.toString());

  const queryString = queryParams.toString();
  const endpoint = queryString
    ? `/internal/apis/v4/${apiRole}/medicine-subscribers/${bookingId}/snapshot/?${queryString}`
    : `/internal/apis/v4/${apiRole}/medicine-subscribers/${bookingId}/snapshot/`;

  return makeRequest(endpoint);
};

// Get medicine booking detail
export const getMedicineBookingDetail = async (role, bookingId) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/medicine-subscribers/${bookingId}/detail/`;

  return makeRequest(endpoint);
};

// Medicine order refund (Ops, Finance and SuperAdmin only). Refundable when status is ordered, processed, dispatched, out_for_delivery, delivered.
export const refundMedicineOrder = async (role, bookingId, body) => {
  if (role !== 'Finance' && role !== 'SuperAdmin' && role !== 'Ops') {
    throw new Error('Access denied. Only Ops, Finance and SuperAdmin can refund medicine orders.');
  }
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/medicine-subscribers/${bookingId}/refund/`;
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      refund_reason: (body.refund_reason || '').trim(),
      confirmation_text: body.confirmation_text === 'REFUND' ? 'REFUND' : (body.confirmation_text || '')
    })
  });
};

// ============================================================================
// Manual Service Orders API
// ============================================================================

// Get service providers for manual service orders
export const getManualServiceProviders = async (role, serviceType = 'medicine') => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/providers/list/?service_type=${serviceType}`;
  return makeRequest(endpoint);
};

// Note: Medicine catalog operations have been moved to medicineCatalogService.js
// Import from '../services/medicineCatalogService' instead

// Search service catalog (medicines/diagnostics)
// Supports: vendor_id (priority 1), order_id (priority 2), service_provider (priority 3)
export const searchManualServiceCatalog = async (role, params = {}) => {
  const apiRole = getApiRole(role);
  const queryParams = new URLSearchParams();
  
  if (params.service_type) queryParams.append('service_type', params.service_type);
  
  // Priority 1: Use vendor_id if available (most accurate)
  if (params.vendor_id) {
    queryParams.append('vendor_id', params.vendor_id);
  }
  // Priority 2: Use order_id (backend gets vendor automatically)
  else if (params.order_id) {
    queryParams.append('order_id', params.order_id);
  }
  // Priority 3: Fallback to service_provider name
  else if (params.service_provider) {
    queryParams.append('service_provider', params.service_provider);
  }
  
  if (params.q) queryParams.append('q', params.q);
  
  const queryString = queryParams.toString();
  const endpoint = queryString
    ? `/internal/apis/v4/${apiRole}/manual-service/catalog/search/?${queryString}`
    : `/internal/apis/v4/${apiRole}/manual-service/catalog/search/`;
  
  return makeRequest(endpoint);
};

// Create manual service order
export const createManualServiceOrder = async (role, orderData) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/order/`;
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      action: 'create',
      ...orderData
    })
  });
};

// Add item to order
export const addItemToManualServiceOrder = async (role, orderId, itemData) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/order/${orderId}/item/`;
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      action: 'add',
      ...itemData
    })
  });
};

// Update item in order
export const updateItemInManualServiceOrder = async (role, orderId, itemData) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/order/${orderId}/item/`;
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      action: 'update',
      ...itemData
    })
  });
};

// Remove item from order
export const removeItemFromManualServiceOrder = async (role, orderId, itemId) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/order/${orderId}/item/`;
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      action: 'remove',
      item_id: itemId
    })
  });
};

// Mark item(s) out of stock
export const markItemsOutOfStock = async (role, orderId, itemData) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/order/${orderId}/item/`;
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      action: 'mark_out_of_stock',
      ...itemData
    })
  });
};

export const previewManualServiceOrderCashback = async (role, orderId, items) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/order/`;
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      action: 'preview_cashback',
      order_id: orderId,
      items
    })
  });
};

// Save manual service order
export const saveManualServiceOrder = async (role, orderId, orderData = {}) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/order/`;
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      action: 'save',
      order_id: orderId,
      ...orderData
    })
  });
};

// Submit manual service order
export const submitManualServiceOrder = async (role, orderId) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/order/`;
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      action: 'submit',
      order_id: orderId
    })
  });
};

// Get order detail
export const getManualServiceOrderDetail = async (role, orderId) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/order/${orderId}/detail/`;
  return makeRequest(endpoint);
};

// Get prefilled cart for manual service order
export const getPrefilledCart = async (role, applicationId, walletTransactionId = null) => {
  const apiRole = getApiRole(role);
  let endpoint;
  
  // Priority 1: Use wallet_transaction_id if provided (recommended for medicine subscriber snapshot)
  if (walletTransactionId) {
    endpoint = `/internal/apis/v4/${apiRole}/manual-service/cart/prefilled/?wallet_transaction_id=${walletTransactionId}`;
  } else if (applicationId) {
    // Fallback: Use application_id
    endpoint = `/internal/apis/v4/${apiRole}/manual-service/cart/prefilled/?application_id=${applicationId}`;
  } else {
    throw new Error('application_id or wallet_transaction_id is required');
  }
  
  return makeRequest(endpoint);
};

// Update order note
export const updateOrderNote = async (role, orderId, orderNote) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/order/${orderId}/?action=update_note`;
  const requestBody = {
    order_note: orderNote
  };
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(requestBody)
  });
};

// Cancel order with reason
export const cancelOrder = async (role, orderId, cancellationReason) => {
  const apiRole = getApiRole(role);
  // Use  Include action in request body (more reliable)
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/order/${orderId}/`;
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      action: 'cancel',
      cancellation_reason: cancellationReason
    })
  });
};

// Mark all items as out of stock
export const markAllItemsOutOfStock = async (role, orderId, orderNote = null, deliveryCharges = null, taxes = null) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/manual-service/order/${orderId}/?action=mark_all_out_of_stock`;
  const requestBody = {};
  
  if (orderNote !== null && orderNote !== '') {
    requestBody.order_note = orderNote;
  }
  if (deliveryCharges !== null) {
    requestBody.delivery_charges = deliveryCharges;
  }
  if (taxes !== null) {
    requestBody.taxes = taxes;
  }
  
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(requestBody)
  });
};

// Update medicine booking status
export const updateMedicineBookingStatus = async (role, bookingId, status, thirdPartyOrderId = null) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/medicine-subscribers/${bookingId}/update-status/`;
  const requestBody = {
    status: status.toLowerCase()
  };

  // Add third_party_order_id only for processed status
  if (status.toLowerCase() === 'processed' && thirdPartyOrderId) {
    requestBody.third_party_order_id = thirdPartyOrderId;
  }

  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(requestBody)
  });
};

// Update diagnostic booking status (Ops and SuperAdmin only) — for AyushPay-owned
// (non-partner) lab bookings. Advances status: initiated → confirmed →
// sample_collected → completed. Mirrors updateMedicineBookingStatus.
// NOTE: backend endpoint is NOT implemented yet — the caller keeps this behind an
// off-by-default feature flag (DIAGNOSTIC_STATUS_UPDATE_ENABLED) until it ships.
export const updateDiagnosticBookingStatus = async (role, transactionId, status) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/diagnostic-subscribers/${transactionId}/update-status/`;
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify({ status: status.toLowerCase() })
  });
};

// Attach a report link to a diagnostic booking (Ops and SuperAdmin only).
// Appends — the backend never replaces, because one booking can cover several family
// members and so several reports. Partner (Healthians) bookings are refused: their
// reports arrive from the lab's own webhook.
export const updateDiagnosticBookingReport = async (role, transactionId, reportUrl) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/diagnostic-subscribers/${transactionId}/update-report/`;
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify({ report_url: reportUrl })
  });
};

// Send one attached report link to the subscriber over WhatsApp (Ops and SuperAdmin only).
// The URL must be one already attached to the booking — the backend validates it rather
// than sending whatever it is handed. Ops picks WHICH report, because the report list
// carries no per-member association and guessing could send one relative's results to
// another's phone.
// NOTE: this returns an error until Meta approves a template carrying a url button; the
// backend says so explicitly rather than failing silently.
export const sendDiagnosticBookingReport = async (role, transactionId, reportUrl) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/diagnostic-subscribers/${transactionId}/send-report/`;
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify({ report_url: reportUrl })
  });
};

// ============================================================================
// Account Deletion Requests APIs (Ops and SuperAdmin only)
// ============================================================================

// Get account deletion requests list
export const getAccountDeletionRequestsList = async (role, params = {}) => {
  const apiRole = getApiRole(role);
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', params.page.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.status) queryParams.append('status', params.status);

  const queryString = queryParams.toString();
  const endpoint = queryString
    ? `/internal/apis/v4/${apiRole}/account-delete-requests/list/?${queryString}`
    : `/internal/apis/v4/${apiRole}/account-delete-requests/list/`;

  return makeRequest(endpoint);
};

// Update account deletion request status
export const updateAccountDeletionRequestStatus = async (role, requestId, status, notes = '') => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/account-delete-requests/${requestId}/update-status/`;
  return makeRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify({ status, notes })
  });
};

// ============================================================================
// Partner Enquiry (Ayushpay Leads) APIs (Ops and SuperAdmin only)
// ============================================================================

export const getPartnerEnquiryList = async (role, params = {}) => {
  const apiRole = getApiRole(role);
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', params.page.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.city) queryParams.append('city', params.city);
  if (params.state) queryParams.append('state', params.state);
  if (params.consent) queryParams.append('consent', params.consent);

  const queryString = queryParams.toString();
  const endpoint = queryString
    ? `/internal/apis/v4/${apiRole}/partner-enquiries/list/?${queryString}`
    : `/internal/apis/v4/${apiRole}/partner-enquiries/list/`;

  return makeRequest(endpoint);
};

// ============================================================================
// Subscriber Documents APIs (Ops and SuperAdmin only)
// ============================================================================

export const getSubscriberDocuments = async (applicationId, role) => {
  const apiRole = getApiRole(role);
  const endpoint = `/internal/apis/v4/${apiRole}/applications/${applicationId}/documents/`;
  return makeRequest(endpoint);
};

// ============================================================================
// Wallet Activation APIs (Ops and SuperAdmin only)
// ============================================================================

export const getWalletActivationDetail = async (applicationId, role) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/applications/${applicationId}/wallet-activation/detail/`);
};

export const generateWalletActivationPin = async (applicationId, role) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/applications/${applicationId}/wallet-activation/generate-pin/`, {
    method: 'POST',
  });
};

// ============================================================================
// Manual Diagnostic Order APIs (Ops and SuperAdmin only)
// ============================================================================

export const createDiagnosticOrder = async (role, orderData) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/order/create/`, {
    method: 'POST',
    body: JSON.stringify(orderData),
  });
};

export const getDiagnosticOrderPatients = async (role, orderId) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/order/${orderId}/patients/`);
};

export const selectDiagnosticOrderPatients = async (role, orderId, memberIds) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/order/${orderId}/patients/select/`, {
    method: 'POST',
    body: JSON.stringify({ member_ids: memberIds }),
  });
};

export const searchDiagnosticTests = async (role, orderId, query = '') => {
  const apiRole = getApiRole(role);
  const params = new URLSearchParams({ order_id: orderId });
  if (query) params.append('q', query);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/tests/search/?${params.toString()}`);
};

export const getDiagnosticTestDetail = async (role, orderId, productId) => {
  const apiRole = getApiRole(role);
  const params = new URLSearchParams({ order_id: orderId, product_id: productId });
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/tests/detail/?${params.toString()}`);
};

export const addDiagnosticTestToMember = async (role, orderId, itemData) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/order/${orderId}/item/add/`, {
    method: 'POST',
    body: JSON.stringify(itemData),
  });
};

export const removeDiagnosticTestFromMember = async (role, orderId, itemData) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/order/${orderId}/item/remove/`, {
    method: 'DELETE',
    body: JSON.stringify(itemData),
  });
};

export const getDiagnosticSlots = async (role, orderId, slotDate = null) => {
  const apiRole = getApiRole(role);
  const params = new URLSearchParams({ order_id: orderId });
  if (slotDate) params.append('slot_date', slotDate);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/order/${orderId}/slots/?${params.toString()}`);
};

export const saveDiagnosticSlot = async (role, orderId, slotData) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/order/${orderId}/slots/save/`, {
    method: 'POST',
    body: JSON.stringify(slotData),
  });
};

export const getDiagnosticCart = async (role, orderId) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/order/${orderId}/cart/`);
};

// Save the booking's collection mode (walk-in / home collection). Independent booleans.
export const saveDiagnosticCollection = async (role, orderId, flags) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/order/${orderId}/collection/save/`, {
    method: 'POST',
    body: JSON.stringify(flags),
  });
};

export const submitDiagnosticOrder = async (role, orderId) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/order/${orderId}/submit/`, {
    method: 'POST',
    body: JSON.stringify({ order_id: orderId }),
  });
};

// Generate (or re-send) a Razorpay payment link for a submitted medicine or diagnostic order.
// The amount is computed server-side: the cart total after instant cashback.
export const generateBookingPaymentLink = async (role, orderId) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/order/${orderId}/payment-link/`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
};

export const getDiagnosticAddresses = async (role, applicationId) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/addresses/?application_id=${applicationId}`);
};

export const saveDiagnosticAddress = async (role, addressData) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/addresses/save/`, {
    method: 'POST',
    body: JSON.stringify(addressData),
  });
};

export const updateDiagnosticAddress = async (role, addressId, addressData) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/addresses/${addressId}/update/`, {
    method: 'PATCH',
    body: JSON.stringify(addressData),
  });
};

/**
 * The subscriber's saved addresses, and a new one.
 *
 * These are the canonical names: the endpoints are application-scoped, not diagnostics-scoped —
 * they read and write Address rows hanging off `application.personal`, and the backend's
 * `address_type='diagnostics'` filter is commented out. The `diagnostic/` segment in the URL is
 * legacy path naming, nothing more, which is why the medicine cart calls the same three.
 *
 * `getDiagnosticAddresses` / `saveDiagnosticAddress` remain exported below under their original
 * names so DiagnosticTransaction.jsx is untouched by this change.
 */
export const getApplicationAddresses = async (role, applicationId) => getDiagnosticAddresses(role, applicationId);

export const saveApplicationAddress = async (role, addressData) => saveDiagnosticAddress(role, addressData);

export const checkDiagnosticServiceability = async (role, addressId, applicationId) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/addresses/check-serviceability/`, {
    method: 'POST',
    body: JSON.stringify({ address_id: addressId, application_id: applicationId }),
  });
};

export const getDiagnosticVendors = async (role) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/vendors/`);
};

export const getDiagnosticExistingOrder = async (role, applicationId, vendorId) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/manual-service/diagnostic/order/get/?application_id=${applicationId}&vendor_id=${vendorId}`);
};
